import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCommercialProjectRow } from "./commercialProjectAcf";
import {
  collectCustomerOptions,
  collectSalesRepOptions,
  createCommercialProject,
  fetchBundledCustomerList,
  mergeCustomerOptionLists,
} from "./addProjectApi";
import {
  canCreateCommercialProject,
  listMatrixUserEmailsByRoleName,
} from "./commercialRoleAccess";
import {
  CONNECTION_OPTIONS,
  DEPARTMENT_OPTIONS,
  IP_OPTIONS,
  PROJECT_SUMMARY_SUBTYPE_OPTIONS,
  PROJECT_SUMMARY_TYPE_OPTIONS,
  SERVICE_SPEED_OPTIONS,
  serviceSpeedLabel,
} from "./commercialProjectSchema";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import CustomerPicker from "./CustomerPicker";
import "./addProjectPage.css";

const EMPTY_LOCATION = {
  departments: [],
  service_location: "",
  connection_types: [],
  a_location: "",
  z_location: "",
  ips: "",
  service_speed: "",
  mrc: "",
  nrc: "",
  subtype: "",
  notes: "",
  site_contact_name: "",
  site_contact_email: "",
  site_contact_phone: "",
  it_contact: "",
  it_email: "",
  it_number: "",
};

const EMPTY_FORM = {
  project_type: "Revenue",
  customer: "",
  new_customer: false,
  term: "",
  sales_rep: "",
  accounting_contact: "",
  accounting_email: "",
  accounting_phone: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  locations: [{ ...EMPTY_LOCATION }],
};

function FormField({ label, children, hint }) {
  return (
    <div className="add-project-field">
      <label>{label}</label>
      {children}
      {hint ? (
        <span className="project-detail-muted" style={{ fontSize: 12 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function CheckboxMultiSelect({ label, options, value, onChange, disabled, required }) {
  const selected = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
  return (
    <FormField label={label}>
      <div className="add-project-departments commercial-multiselect">
        {options.map((opt) => (
          <label key={opt} className="commercial-multiselect-option">
            <input
              type="checkbox"
              checked={selected.has(opt)}
              disabled={disabled}
              required={required && selected.size === 0}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(opt);
                else next.delete(opt);
                onChange([...next]);
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    </FormField>
  );
}

function locationHasPtpOrDark(location) {
  const types = Array.isArray(location.connection_types) ? location.connection_types : [];
  return types.some((t) => t === "PTP" || t === "Dark");
}

function locationHasDia(location) {
  const types = Array.isArray(location.connection_types) ? location.connection_types : [];
  return types.includes("DIA");
}

function AddProjectForm({ apiUrl, getAuthHeaders }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [signedServiceOrderFile, setSignedServiceOrderFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [salesRepOptions, setSalesRepOptions] = useState([]);

  const matrixSalesReps = useMemo(() => listMatrixUserEmailsByRoleName("Sales"), []);

  const loadOptions = useCallback(async () => {
    try {
      const base = apiUrl || getApiBase();
      const [projectsRes, bundledCustomers] = await Promise.all([
        axios.get(`${base}/projects`, { headers: getAuthHeaders() }),
        fetchBundledCustomerList(),
      ]);
      const rows = Array.isArray(projectsRes.data) ? projectsRes.data.filter(isCommercialProjectRow) : [];
      setCustomerOptions(
        mergeCustomerOptionLists(collectCustomerOptions(rows), bundledCustomers)
      );
      const fromProjects = collectSalesRepOptions(rows);
      const merged = new Set([...fromProjects, ...matrixSalesReps]);
      setSalesRepOptions([...merged].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })));
    } catch {
      setCustomerOptions([]);
      setSalesRepOptions([...matrixSalesReps]);
    }
  }, [apiUrl, getAuthHeaders, matrixSalesReps]);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  const isRevenue = form.project_type === "Revenue";
  const isMultisite = form.locations.length > 1;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setProjectType = (projectType) => {
    setForm((prev) => ({
      ...prev,
      project_type: projectType,
      ...(projectType === "Revenue"
        ? {}
        : {
            new_customer: false,
            term: "",
            sales_rep: "",
            locations: prev.locations.map((loc) => ({
              ...loc,
              connection_types: [],
              a_location: "",
              z_location: "",
              ips: "",
              service_speed: "",
              mrc: "",
              nrc: "",
              subtype: "",
            })),
          }),
    }));
    if (projectType !== "Revenue") {
      setSignedServiceOrderFile(null);
    }
  };

  const updateLocation = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) => (i === index ? { ...loc, ...patch } : loc)),
    }));
  };

  const addLocation = () => {
    setForm((prev) => ({
      ...prev,
      locations: [...prev.locations, { ...EMPTY_LOCATION }],
    }));
  };

  const removeLocation = (index) => {
    setForm((prev) => {
      if (prev.locations.length <= 1) return prev;
      return { ...prev, locations: prev.locations.filter((_, i) => i !== index) };
    });
  };

  const validateForm = () => {
    for (let i = 0; i < form.locations.length; i += 1) {
      const loc = form.locations[i];
      const label = form.locations.length > 1 ? `Location ${i + 1}` : "Location";
      if (!loc.departments?.length) {
        return `${label}: select at least one department.`;
      }
      if (!isRevenue) continue;
      if (!String(loc.service_location || "").trim()) {
        return `${label}: service location is required.`;
      }
      if (!loc.connection_types?.length) {
        return `${label}: select at least one service type.`;
      }
      if (locationHasDia(loc)) {
        if (!String(loc.service_speed || "").trim()) {
          return `${label}: service speed is required for DIA.`;
        }
        if (!String(loc.ips || "").trim()) {
          return `${label}: IPs selection is required for DIA.`;
        }
      }
      if (locationHasPtpOrDark(loc)) {
        if (!String(loc.a_location || "").trim() || !String(loc.z_location || "").trim()) {
          return `${label}: A and Z locations are required for PTP or Dark.`;
        }
      }
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const data = await createCommercialProject(form, {
        apiUrl,
        getAuthHeaders,
        signedServiceOrderFile: isRevenue ? signedServiceOrderFile : null,
      });
      if (data?.multisite && data?.work_order != null) {
        navigate(`/work-order/${data.work_order}`);
        return;
      }
      const projectId = data?.project?.id;
      if (projectId != null) {
        navigate(`/project/${projectId}`);
        return;
      }
      setError("Project was created but no project id was returned.");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not create project.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="add-project-form" onSubmit={(e) => void submit(e)}>
      <section className="add-project-section">
        <h3 className="add-project-section-title">Project type</h3>
        <FormField label="Work order type">
          <select
            value={form.project_type}
            required
            disabled={saving}
            onChange={(e) => setProjectType(e.target.value)}
          >
            {PROJECT_SUMMARY_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </FormField>
      </section>

      <section className="add-project-section">
        <h3 className="add-project-section-title">Customer</h3>
        <FormField label="Customer" hint="Search existing customers or add a new one if it is not listed.">
          <CustomerPicker
            value={form.customer}
            options={customerOptions}
            required
            disabled={saving}
            onChange={(name) => setField("customer", name)}
          />
        </FormField>
        {isRevenue ? (
          <FormField label="New customer" hint="When checked, a New customer email is sent after the project is created.">
            <label className="commercial-multiselect-option" style={{ display: "inline-flex", gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(form.new_customer)}
                disabled={saving}
                onChange={(e) => setField("new_customer", e.target.checked)}
              />
              This is a new customer
            </label>
          </FormField>
        ) : null}
        {isMultisite ? (
          <>
            <FormField
              label="Primary customer contact"
              hint="Optional order-level contact stored on the parent work order."
            >
              <input
                type="text"
                value={form.contact_name}
                disabled={saving}
                onChange={(e) => setField("contact_name", e.target.value)}
              />
            </FormField>
            <div className="add-project-row">
              <FormField label="Contact email">
                <input
                  type="email"
                  value={form.contact_email}
                  disabled={saving}
                  onChange={(e) => setField("contact_email", e.target.value)}
                />
              </FormField>
              <FormField label="Contact phone">
                <input
                  type="tel"
                  value={form.contact_phone}
                  disabled={saving}
                  onChange={(e) => setField("contact_phone", e.target.value)}
                />
              </FormField>
            </div>
          </>
        ) : null}
      </section>

      <section className="add-project-section">
        <h3 className="add-project-section-title">Accounting contact</h3>
        <FormField label="Contact name">
          <input
            type="text"
            value={form.accounting_contact}
            disabled={saving}
            onChange={(e) => setField("accounting_contact", e.target.value)}
          />
        </FormField>
        <div className="add-project-row">
          <FormField label="Email">
            <input
              type="email"
              value={form.accounting_email}
              disabled={saving}
              onChange={(e) => setField("accounting_email", e.target.value)}
            />
          </FormField>
          <FormField label="Phone">
            <input
              type="tel"
              value={form.accounting_phone}
              disabled={saving}
              onChange={(e) => setField("accounting_phone", e.target.value)}
            />
          </FormField>
        </div>
      </section>

      {isRevenue ? (
        <>
          <section className="add-project-section">
            <h3 className="add-project-section-title">Commercial terms</h3>
            <div className="add-project-row">
              <FormField label="Term (months)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.term}
                  disabled={saving}
                  onChange={(e) => setField("term", e.target.value)}
                />
              </FormField>
            </div>
            <FormField label="Sales rep">
              <select
                value={form.sales_rep}
                disabled={saving}
                onChange={(e) => setField("sales_rep", e.target.value)}
              >
                <option value="">— Select sales rep —</option>
                {salesRepOptions.map((rep) => (
                  <option key={rep} value={rep}>
                    {rep}
                  </option>
                ))}
              </select>
            </FormField>
            {isMultisite ? (
              <p className="project-detail-muted" style={{ margin: 0, fontSize: 13 }}>
                MRC and NRC are entered per location below. Term and sales rep apply to the entire service order.
              </p>
            ) : null}
          </section>

          <section className="add-project-section">
            <h3 className="add-project-section-title">Service order</h3>
            <FormField label="Upload service order (PDF)" hint="Optional. Attached to the new project after creation.">
              <input
                type="file"
                accept=".pdf,application/pdf"
                disabled={saving}
                onChange={(e) => setSignedServiceOrderFile(e.target.files?.[0] || null)}
              />
              {signedServiceOrderFile ? (
                <span className="project-detail-muted" style={{ fontSize: 12 }}>
                  Selected: {signedServiceOrderFile.name}
                </span>
              ) : null}
            </FormField>
          </section>
        </>
      ) : null}

      <section className="add-project-section">
        <div className="add-project-section-header">
          <h3 className="add-project-section-title">Locations</h3>
          <button
            type="button"
            className="add-project-add-location"
            disabled={saving}
            onClick={addLocation}
          >
            + Add location
          </button>
        </div>
        {isMultisite ? (
          <p className="project-detail-muted" style={{ margin: 0, fontSize: 13 }}>
            Each location gets its own departments, site/IT contacts, and sub-work order (e.g. 14120.01).
          </p>
        ) : null}
        {form.locations.map((loc, index) => (
          <div key={index} className="add-project-location-card">
            {form.locations.length > 1 ? (
              <div className="add-project-location-header">
                <strong>Location {index + 1}</strong>
                <button
                  type="button"
                  className="add-project-remove-location"
                  disabled={saving}
                  onClick={() => removeLocation(index)}
                >
                  Remove
                </button>
              </div>
            ) : null}
            <CheckboxMultiSelect
              label="Departments involved"
              options={DEPARTMENT_OPTIONS}
              value={loc.departments}
              disabled={saving}
              required
              onChange={(deps) => updateLocation(index, { departments: deps })}
            />
            <FormField label="Service location">
              <textarea
                value={loc.service_location}
                required={isRevenue}
                disabled={saving}
                rows={2}
                onChange={(e) => updateLocation(index, { service_location: e.target.value })}
              />
            </FormField>
            {isRevenue ? (
              <FormField label="Subtype">
                <select
                  value={loc.subtype || ""}
                  disabled={saving}
                  onChange={(e) => updateLocation(index, { subtype: e.target.value })}
                >
                  <option value="">— Select subtype —</option>
                  {PROJECT_SUMMARY_SUBTYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </FormField>
            ) : null}
            <FormField label="Notes">
              <textarea
                value={loc.notes}
                disabled={saving}
                rows={3}
                placeholder="Optional notes for this location"
                onChange={(e) => updateLocation(index, { notes: e.target.value })}
              />
            </FormField>
            <FormField label="Site contact name">
              <input
                type="text"
                value={loc.site_contact_name}
                disabled={saving}
                onChange={(e) => updateLocation(index, { site_contact_name: e.target.value })}
              />
            </FormField>
            <div className="add-project-row">
              <FormField label="Site email">
                <input
                  type="email"
                  value={loc.site_contact_email}
                  disabled={saving}
                  onChange={(e) => updateLocation(index, { site_contact_email: e.target.value })}
                />
              </FormField>
              <FormField label="Site phone">
                <input
                  type="tel"
                  value={loc.site_contact_phone}
                  disabled={saving}
                  onChange={(e) => updateLocation(index, { site_contact_phone: e.target.value })}
                />
              </FormField>
            </div>
            <FormField label="IT contact name">
              <input
                type="text"
                value={loc.it_contact}
                disabled={saving}
                onChange={(e) => updateLocation(index, { it_contact: e.target.value })}
              />
            </FormField>
            <div className="add-project-row">
              <FormField label="IT email">
                <input
                  type="email"
                  value={loc.it_email}
                  disabled={saving}
                  onChange={(e) => updateLocation(index, { it_email: e.target.value })}
                />
              </FormField>
              <FormField label="IT phone">
                <input
                  type="tel"
                  value={loc.it_number}
                  disabled={saving}
                  onChange={(e) => updateLocation(index, { it_number: e.target.value })}
                />
              </FormField>
            </div>
            {isRevenue ? (
              <>
                <CheckboxMultiSelect
                  label="Service types"
                  options={CONNECTION_OPTIONS}
                  value={loc.connection_types}
                  disabled={saving}
                  required
                  onChange={(types) => updateLocation(index, { connection_types: types })}
                />
                {locationHasPtpOrDark(loc) ? (
                  <>
                    <FormField label="A location">
                      <textarea
                        value={loc.a_location}
                        disabled={saving}
                        rows={2}
                        onChange={(e) => updateLocation(index, { a_location: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Z location">
                      <textarea
                        value={loc.z_location}
                        disabled={saving}
                        rows={2}
                        onChange={(e) => updateLocation(index, { z_location: e.target.value })}
                      />
                    </FormField>
                  </>
                ) : null}
                {locationHasDia(loc) ? (
                  <div className="add-project-row">
                    <FormField label="Service speed">
                      <select
                        value={loc.service_speed}
                        disabled={saving}
                        onChange={(e) => updateLocation(index, { service_speed: e.target.value })}
                      >
                        <option value="">— Select speed —</option>
                        {SERVICE_SPEED_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="IPs">
                      <select
                        value={loc.ips}
                        disabled={saving}
                        onChange={(e) => updateLocation(index, { ips: e.target.value })}
                      >
                        <option value="">— Select IPs —</option>
                        {IP_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            /{opt}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                ) : null}
                <div className="add-project-row">
                  <FormField label="MRC">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={loc.mrc}
                      disabled={saving}
                      onChange={(e) => updateLocation(index, { mrc: e.target.value })}
                    />
                  </FormField>
                  <FormField label="NRC">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={loc.nrc}
                      disabled={saving}
                      onChange={(e) => updateLocation(index, { nrc: e.target.value })}
                    />
                  </FormField>
                </div>
              </>
            ) : null}
          </div>
        ))}
      </section>

      {!isRevenue ? (
        <p className="project-detail-muted" style={{ margin: 0, fontSize: 13 }}>
          Commercial terms and circuit details are not required for Non-Revenue or Capital projects.
        </p>
      ) : null}

      {error ? (
        <p className="project-detail-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="add-project-actions">
        <button type="submit" className="add-project-submit" disabled={saving}>
          {saving ? "Creating…" : isMultisite ? "Create multisite order" : "Create project"}
        </button>
        <Link to="/" className="project-detail-muted">
          Cancel
        </Link>
      </div>
    </form>
  );
}

export default function AddProjectPage({ apiUrl, getAuthHeaders }) {
  const viewerEmail = getViewerEmailFromStorage();
  const allowed = canCreateCommercialProject(viewerEmail);

  if (!allowed) {
    return (
      <div className="add-project-page">
        <div className="add-project-page-inner">
          <p className="project-detail-muted">
            Add project is limited to Coordinators and Administrators.
          </p>
          <Link to="/">Back to board</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="add-project-page">
      <div className="add-project-page-inner">
        <h2 className="add-project-title">Add project</h2>
        <p className="add-project-subtitle">
          Create a new commercial project (Revenue, Non-Revenue, or Capital). Work order number is assigned
          automatically. Add multiple locations to create a multisite service order with sub-work orders
          (e.g. 14120.01). Revenue projects start in Sales; Non-Revenue and Capital start in Finance.
        </p>
        <AddProjectForm apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />
      </div>
    </div>
  );
}

export function AddProjectGate(props) {
  return <AddProjectPage {...props} />;
}
